import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Instagram, Loader2, MessageCircle, Send } from "lucide-react";
import { Logo } from "./Logo";
import { useLang } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LINKS = [
  { id: "footer-link-services", href: "#services" },
  { id: "footer-link-colors", href: "#colors" },
  { id: "footer-link-brochures", href: "#brochures" },
  { id: "footer-link-works", href: "#works" },
  { id: "footer-link-warranty", href: "#warranty" },
  { id: "footer-link-contact", href: "#contact" },
  { id: "footer-link-warranty-portal", href: "https://5aegis.com", external: true },
];

export default function Footer() {
  const { t, lang } = useLang();
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await axios.post(`${API}/newsletter`, { email });
      toast.success(
        res.data?.message === "already_subscribed" ? t.footer.alreadyToast : t.footer.subscribedToast
      );
      setEmail("");
    } catch {
      toast.error(t.footer.errorToast);
    } finally {
      setSending(false);
    }
  };

  return (
    <footer className="border-t border-[#F5F2EB]/10 bg-[#030303]" data-testid="site-footer">
      <div className="mx-auto grid max-w-7xl gap-12 px-5 py-16 sm:px-8 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <Logo />
          <p className="mt-5 max-w-sm text-sm leading-relaxed text-[#A8A29E]">
            {t.footer.brandText}
          </p>
          <div className="mt-6 flex items-center gap-3">
            <a
              href="https://instagram.com/connected_center_basra"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-instagram-link"
              aria-label="Instagram"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-[#F5F2EB]/15 text-[#A8A29E] transition-all duration-300 hover:border-[#E3AF4F] hover:text-[#E3AF4F]"
            >
              <Instagram size={16} />
            </a>
            <a
              href="https://wa.me/9647760506057"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-whatsapp-link"
              aria-label="WhatsApp"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-[#F5F2EB]/15 text-[#A8A29E] transition-all duration-300 hover:border-[#E3AF4F] hover:text-[#E3AF4F]"
            >
              <MessageCircle size={16} />
            </a>
          </div>
        </div>
        <div className="lg:col-span-3">
          <h3 className="font-heading text-sm font-bold text-[#F5F2EB]">{t.footer.quickLinks}</h3>
          <ul className="mt-5 space-y-3">
            {LINKS.map((l, i) => (
              <li key={l.id}>
                <a
                  data-testid={l.id}
                  href={l.href}
                  {...(l.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                  className="text-sm text-[#A8A29E] transition-colors duration-300 hover:text-[#E3AF4F]"
                >
                  {t.footer.links[i]}
                </a>
              </li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-4">
          <h3 className="font-heading text-sm font-bold text-[#F5F2EB]">{t.footer.newsletterTitle}</h3>
          <p className="mt-4 text-sm leading-relaxed text-[#A8A29E]">{t.footer.newsletterText}</p>
          <form onSubmit={subscribe} className="mt-5 flex gap-2">
            <input
              type="email"
              required
              dir="ltr"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="newsletter-email-input"
              placeholder={t.footer.placeholder}
              className={`w-full rounded-full border border-[#F5F2EB]/10 bg-[#0E0E0E] px-5 py-3 text-sm text-[#F5F2EB] placeholder:text-[#A8A29E]/50 focus:border-[#E3AF4F] focus:outline-none focus:ring-1 focus:ring-[#E3AF4F] ${
                lang === "ar" ? "placeholder:text-right" : "placeholder:text-left"
              }`}
            />
            <button
              type="submit"
              data-testid="newsletter-submit-button"
              disabled={sending}
              aria-label={t.footer.subscribeAria}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#E3AF4F] text-[#000000] transition-colors duration-300 hover:bg-[#F5D08C] disabled:opacity-60"
            >
              {sending ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Send size={16} className={lang === "ar" ? "-scale-x-100" : ""} />
              )}
            </button>
          </form>
        </div>
      </div>
      <div className="border-t border-[#F5F2EB]/10 py-6 text-center text-xs text-[#A8A29E]">
        {t.footer.copyright}
      </div>
    </footer>
  );
}
